import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import Shine from '@sorare/core/src/atoms/ui/Shine';
import { isType } from '@sorare/core/src/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { createVar } from '@sorare/core/src/style/utils';

import { BadgeOverview_ManagerProgressionCategory } from './__generated__/index.graphql';
import { PARAMETERS_PER_CATEGORY } from './utils';

const badge = createVar();
const colorFrom = createVar();
const colorTo = createVar();

const Root = styled(Vertical).attrs({ center: true })`
  position: absolute;
  inset: 0;
`;
const fadeOut = keyframes`
0% {
  opacity: 1;
}
100% {
  opacity: 0;
}`;
const Content = styled(Vertical).attrs({ center: true })`
  --badge-size: 60vw;
  --frame-spacing: 50px;
  text-align: center;
  position: relative;
  gap: calc(var(--frame-spacing) + var(--quadruple-unit));

  animation: 0.3s linear 1.6s forwards ${fadeOut};

  @media ${tabletAndAbove} {
    --badge-size: 300px;
  }
`;
const Main = styled.div`
  position: relative;
`;
const scaleIn = keyframes`
0% {
  transform: scale(4);
}
100% {
  transform: scale(1);
}`;
const centeredItemCss = css`
  width: var(--size);
  aspect-ratio: 1/1;
  position: absolute;
  top: calc((var(--badge-size) - var(--size)) / 2);
  left: calc((var(--badge-size) - var(--size)) / 2);
`;
const BadgeContainer = styled.div`
  width: var(--badge-size);
  aspect-ratio: 1/1;

  animation: 0.2s ease-in backwards ${scaleIn};

  mask-image: var(${badge});
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
`;
const Badge = styled.img`
  width: 100%;
  aspect-ratio: 1/1;
`;
const bounce = keyframes`
0% {
  scale: 1;
}
50% {
  scale: 1.3;
}
100% {
  scale: 1;
}`;
const Frame1 = styled.img`
  --size: calc(var(--badge-size) + var(--frame-spacing));
  ${centeredItemCss}

  animation: 1.1s cubic-bezier(0, 0, 0.05, 1.05) backwards ${scaleIn}, .4s ease-in-out 1.5s ${bounce};

  &.reverse {
    rotate: x 180deg;
  }
`;
const Frame2 = styled.img`
  --size: calc(var(--badge-size) + 2 * var(--frame-spacing));
  position: absolute;
  ${centeredItemCss}

  animation: 1.3s cubic-bezier(0, 0, 0.05, 1.05) backwards  ${scaleIn}, .55s ease-in-out 1.5s ${bounce};
`;
const fadeOutLight = keyframes`
0% {
  transform: scale(4);
}
80% {
  opacity: 0;
}
100% {
  transform: scale(1);
  opacity: 0;
}`;
const Light = styled.div`
  --size: 600px;
  ${centeredItemCss}

  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.9) 60%,
    rgba(0, 0, 0, 0) 80%
  );
  filter: blur(30px);
  animation: 0.6s forwards ${fadeOutLight};
`;
const fadeInSmoke = keyframes`
0% {
  transform: scale(1);
}
100% {
  transform: scale(3);
}`;
const Smoke = styled.div`
  --size: 400px;
  ${centeredItemCss}

  background: radial-gradient(
    ellipse 150px 200px,
    var(${colorFrom}) 10%,
    var(${colorTo}) 20%,
    rgba(0, 0, 0, 0) 70%
  );
  filter: blur(20px);
  animation: 0.6s forwards 0.1s ${fadeInSmoke};
`;
const scaleAndFadeIn = keyframes`
0% {
  opacity: 0;
  transform: scale(3);
}
100% {
  opacity: 1;
  transform: scale(1);
}`;
const commonTextCss = css`
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  animation: 0.6s cubic-bezier(0, 0, 0.05, 1.05) 0.4s both ${scaleAndFadeIn};
`;
const Category = styled.p`
  ${commonTextCss}
  font-size: 4vmin;
`;
const Level = styled.p`
  ${commonTextCss}
  font-size: 3vmin;
`;

type Props = {
  category: BadgeOverview_ManagerProgressionCategory;
  onEnd: () => void;
};

export const BadgeOverview = ({ category, onEnd }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleAnimationEnd = useCallback(
    (event: AnimationEvent) => {
      if (event.animationName.includes(fadeOut.getName())) {
        onEnd();
      }
    },
    [onEnd]
  );

  useEffect(() => {
    if (ref.current) {
      const el = ref.current;
      el.addEventListener('animationend', handleAnimationEnd);
      return () => el.removeEventListener('animationend', handleAnimationEnd);
    }

    return () => {};
  }, [handleAnimationEnd]);

  const tasks = category.categoryTasks.filter(task =>
    isType(task, 'ManagerProgressionTask')
  );
  const nbTasksClaimed = tasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.CLAIMED
  ).length;

  const parameters = PARAMETERS_PER_CATEGORY[category.name];

  return (
    <Root>
      <Content ref={ref}>
        <Main>
          <Smoke
            style={{
              [colorFrom]: parameters?.colorFrom,
              [colorTo]: parameters?.colorTo,
            }}
          />
          <BadgeContainer
            style={{
              [badge]: `url(${category.badgeUrl})`,
            }}
          >
            <Shine>
              <Badge src={category.badgeUrl} alt="" />
            </Shine>
          </BadgeContainer>
          {parameters?.frameUrl && (
            <>
              <Frame1
                src={parameters?.frameUrl}
                alt=""
                className={classNames({ reverse: !parameters?.notReverse })}
              />
              <Frame2 src={parameters?.frameUrl} alt="" />
            </>
          )}
          <Light />
        </Main>
        <Vertical gap={0}>
          <Category>{category.title}</Category>
          <Level>
            {nbTasksClaimed} / {tasks.length}
          </Level>
        </Vertical>
      </Content>
    </Root>
  );
};

BadgeOverview.fragments = {
  managerProgressionCategory: gql`
    fragment BadgeOverview_ManagerProgressionCategory on ManagerProgressionCategory {
      id
      name
      title
      badgeUrl
      categoryTasks {
        ... on ManagerTaskInterface {
          id
          aasmState
        }
      }
    }
  ` as TypedDocumentNode<BadgeOverview_ManagerProgressionCategory>,
};
